import { timestampDate } from '@bufbuild/protobuf/wkt'
import type {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ISetFilterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import uniq from 'lodash/uniq'
import { CheckIcon, InfoIcon, LockOpenIcon, XIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import {
  SmartEvidenceResponseType,
  SmartQuestion,
  SmartQuestionStatus,
} from '@/gen/inventory/v1/control_service_pb'

import {
  controlStatusLabel,
  smartEvidenceResponseTypeLabel,
  smartQuestionStatusLabel,
} from '@/const/label'

import { relativeTimeFormatted } from '@/lib/date/date'

import { NO_RESPONSE_LABEL, VENDOR_COMMENT_FILTER } from '@/pages/assessment/labels'
import { smartQuestionStatusComparator } from '@/pages/assessment/smart-question-status-comparator'
import { ControlScopeCell } from '@/pages/control/ag-grid-control/components/cells/control-scope-cell'
import { ControlStatusCell } from '@/pages/control/ag-grid-control/components/cells/control-status-cell'
import { ControlTitleCell } from '@/pages/control/ag-grid-control/components/cells/control-title-cell'
import { ControlWeightCell } from '@/pages/control/ag-grid-control/components/cells/control-weight-cell'
import { WIDTH_CONTROL_WEIGHT } from '@/pages/control/ag-grid-control/config/column-sizes'
import { getCommentsColumnDef } from '@/pages/control/ag-grid-control/config/comments-column-def'
import { CONTROL_COLUMN_HEADERS } from '@/pages/control/ag-grid-control/labels'
import type { ControlRow } from '@/pages/control/ag-grid-control/types'

import { Badge, BadgeProps } from '@/components/lemmala-design-system/badge/badge'
import {
  EMPTY_FILTER_VALUE,
  TABLE_COLUMN_WIDTHS,
} from '@/components/lemmala-design-system/table/consts'
import { numericComparator } from '@/components/lemmala-design-system/table/utils/comparators'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

// Column widths
const WIDTH_CONTROL_MIN = 200
const WIDTH_EVIDENCE_REQUEST = 190

const responseTypeIcon: Record<SmartEvidenceResponseType, ReactNode> = {
  [SmartEvidenceResponseType.ACCEPTED]: (
    <CheckIcon className='size-3 text-main-foreground-success' />
  ),
  [SmartEvidenceResponseType.REJECTED]: <XIcon className='size-3 text-main-foreground-danger' />,
  // Guideline 1.4 keeps Commented out of this column, so the BE never sends it.
  [SmartEvidenceResponseType.COMMENTED]: null,
  [SmartEvidenceResponseType.UNSPECIFIED]: null,
}

export const ISSUES_TAB_DEFAULT_VISIBLE_FILTERS: string[] = ['scope', 'status', 'evidenceRequest']

export const getVendorCommentFilterValue = (control?: ControlRow): string =>
  (control?.smartQuestion?.commentCount || 0) > 0
    ? VENDOR_COMMENT_FILTER.hasComments
    : VENDOR_COMMENT_FILTER.noComments

const setFilterParams = (values: string[]): ISetFilterParams =>
  ({
    values,
    suppressMiniFilter: true,
    selectAllOnMiniFilter: true,
    suppressSyncValuesAfterDataChange: true,
  }) as ISetFilterParams

const emptyAwareFilterValues = (values: string[]): string[] =>
  uniq(values.map((value) => value || EMPTY_FILTER_VALUE)).sort((a, b) => a.localeCompare(b))

const smartQuestionStatusToProps: Record<
  SmartQuestionStatus,
  Pick<BadgeProps, 'variant' | 'badgeStyle'>
> = {
  [SmartQuestionStatus.UNSPECIFIED]: {
    variant: 'neutral',
    badgeStyle: 'ghost',
  },
  [SmartQuestionStatus.UNPUBLISHED]: {
    variant: 'neutral',
    badgeStyle: 'ghost',
  },
  [SmartQuestionStatus.PUBLISHED]: {
    variant: 'neutral',
    badgeStyle: 'ghost',
  },
  [SmartQuestionStatus.AWAITING_REVIEW]: {
    variant: 'attention',
    badgeStyle: 'subtle',
  },
  [SmartQuestionStatus.AWAITING_RECIPIENT]: {
    variant: 'promotion',
    badgeStyle: 'ghost',
  },
  [SmartQuestionStatus.RESOLVED]: {
    variant: 'neutral',
    badgeStyle: 'ghost',
  },
  [SmartQuestionStatus.AWAITING_REOPEN]: {
    variant: 'warning',
    badgeStyle: 'subtle',
  },
}

// Single source of truth for how a smart question renders in the cell. Keep
// the filter's values and valueGetter routed through this so filter chips
// match the cell text.
const getSmartQuestionDisplay = (
  smartQuestion?: SmartQuestion,
): { label: string } & Pick<BadgeProps, 'variant' | 'badgeStyle'> => {
  const status = smartQuestion?.status || SmartQuestionStatus.UNSPECIFIED
  if (
    status === SmartQuestionStatus.AWAITING_REVIEW &&
    smartQuestion?.lastResponseType === undefined
  ) {
    return { label: NO_RESPONSE_LABEL, variant: 'neutral', badgeStyle: 'ghost' }
  }
  return { label: smartQuestionStatusLabel[status], ...smartQuestionStatusToProps[status] }
}

/**
 * Get column definitions for unvalidated controls table in Issues tab
 * Shows controls with Gap or No Evidence status
 */
export const getUnvalidatedControlsColumnDefs = (
  hasManagePermissions: boolean,
  isUnshared: boolean,
  controls: ControlRow[] = [],
): ColDef<ControlRow>[] => [
  {
    colId: 'title',
    field: 'title',
    headerName: 'Control',
    minWidth: WIDTH_CONTROL_MIN,
    flex: 1,
    cellRenderer: (params: ICellRendererParams<ControlRow>) => {
      if (!params.data) return null
      return <ControlTitleCell {...params} />
    },
    cellClass: 'py-3',
    sortable: true,
    getQuickFilterText: (params: GetQuickFilterTextParams<ControlRow>) => {
      const { title, description, categoryName } = params.data || {}
      return [title, description, categoryName].join(' ')
    },
    valueGetter: (params: ValueGetterParams<ControlRow>) => params.data?.title || '',
    cellDataType: false,
  },
  {
    colId: 'category',
    headerName: CONTROL_COLUMN_HEADERS.CATEGORY,
    hide: true,
    valueGetter: (params: ValueGetterParams<ControlRow>) =>
      params.data?.categoryName || EMPTY_FILTER_VALUE,
    filter: 'agSetColumnFilter',
    filterParams: setFilterParams(
      emptyAwareFilterValues(controls.map((control) => control.categoryName || '')),
    ),
    cellDataType: false,
  },
  {
    colId: 'weight',
    field: 'controlWeight',
    headerName: 'Weight',
    width: WIDTH_CONTROL_WEIGHT,
    sortable: true,
    comparator: numericComparator,
    cellRenderer: ControlWeightCell,
    cellDataType: false,
  },
  {
    colId: 'scope',
    headerName: CONTROL_COLUMN_HEADERS.SCOPE,
    width: TABLE_COLUMN_WIDTHS.md,
    sortable: true,
    cellRenderer: ControlScopeCell,
    valueGetter: (params: ValueGetterParams<ControlRow>) =>
      params.data?.scope?.name || EMPTY_FILTER_VALUE,
    filter: 'agSetColumnFilter',
    filterParams: setFilterParams(
      emptyAwareFilterValues(controls.map((control) => control.scope?.name ?? '')),
    ),
    cellDataType: false,
  },
  {
    colId: 'status',
    field: 'status',
    headerName: 'Validation status',
    width: TABLE_COLUMN_WIDTHS.lg,
    cellClass: 'py-3',
    cellRenderer: (params: ICellRendererParams<ControlRow>) => {
      if (!params.data) return null

      return (
        <ControlStatusCell
          {...params}
          readOnly={!hasManagePermissions}
          hasManagePermissions={hasManagePermissions}
          hideLastCalculatedDate
          from='assessment-issues-tab'
        />
      )
    },
    sortable: true,
    valueGetter: (params: ValueGetterParams<ControlRow>) => {
      const status = params.data?.status
      return status === undefined ? '' : controlStatusLabel[status]
    },
    filter: 'agSetColumnFilter',
    filterParams: setFilterParams(
      uniq(
        controls.map((control) =>
          control.status === undefined ? '' : controlStatusLabel[control.status as ControlStatus],
        ),
      ),
    ),
    cellDataType: false,
  },
  {
    colId: 'evidenceRequest',
    headerName: 'Smart evidence request',
    headerComponentParams: {
      innerHeaderComponent: () => (
        <div className='flex min-w-0 items-center gap-1'>
          <span className='truncate'>Smart evidence request</span>
          {isUnshared && (
            <Tooltip
              trigger={<InfoIcon className='size-3.5 text-main-foreground-tertiary' />}
              className='flex flex-col gap-1'
            >
              <span className='text-label-small'>The request hasn’t been sent yet</span>
              <span className='text-main-foreground-secondary text-body-small'>
                Share it to allow the third party to access the portal and begin the validation
                process.
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    width: WIDTH_EVIDENCE_REQUEST,
    cellRenderer: (params: ICellRendererParams<ControlRow>) => {
      const smartQuestion = params.data?.smartQuestion
      const { label, variant, badgeStyle } = getSmartQuestionDisplay(smartQuestion)
      const showResponseSubtext =
        smartQuestion?.status === SmartQuestionStatus.AWAITING_REVIEW &&
        smartQuestion.lastResponseType !== undefined

      const badge = (
        <Badge variant={variant} badgeStyle={badgeStyle} textCase='title'>
          {smartQuestion?.status === SmartQuestionStatus.AWAITING_REOPEN ? (
            <TextWithIcon text={label} icon={<LockOpenIcon className='size-3' />} />
          ) : (
            label
          )}
        </Badge>
      )

      if (!showResponseSubtext) return badge

      return (
        <div className='flex flex-col items-start gap-1'>
          {badge}
          <TextWithIcon
            className='text-main-foreground-secondary'
            textClassName='text-body-small'
            icon={responseTypeIcon[smartQuestion.lastResponseType!]}
            text={`${smartEvidenceResponseTypeLabel[smartQuestion.lastResponseType!]}${
              smartQuestion.lastResponseAt
                ? ` · ${relativeTimeFormatted(timestampDate(smartQuestion.lastResponseAt), { compactUnit: true })}`
                : ''
            }`}
          />
        </div>
      )
    },
    sortable: true,
    comparator: smartQuestionStatusComparator,
    initialSort: 'asc',
    valueGetter: (params: ValueGetterParams<ControlRow>) =>
      getSmartQuestionDisplay(params.data?.smartQuestion).label,
    filter: 'agSetColumnFilter',
    filterParams: {
      ...setFilterParams(
        uniq(controls.map((control) => getSmartQuestionDisplay(control.smartQuestion).label)),
      ),
      comparator: (a: string | null, b: string | null) => (a ?? '').localeCompare(b ?? ''),
    } as ISetFilterParams,
    cellDataType: false,
  },
  {
    colId: 'vendorComment',
    headerName: VENDOR_COMMENT_FILTER.header,
    hide: true,
    valueGetter: (params: ValueGetterParams<ControlRow>) =>
      getVendorCommentFilterValue(params.data),
    filter: 'agSetColumnFilter',
    filterParams: setFilterParams([
      VENDOR_COMMENT_FILTER.hasComments,
      VENDOR_COMMENT_FILTER.noComments,
    ]),
    cellDataType: false,
  },
  getCommentsColumnDef(),
]
